<template>
  <div class="page-container">
    <div class="page-title">{{ $t('menu.business_manage') }}</div>
    <div class="filter-section-container">
      <div class="filter-fixed">
        <span class="filter-fixed-title">{{ $t('data.filter_section') }}</span>
        <i class="el-icon-arrow-down filter-fixed-arrow" :class="{ 'fiter-icon-arrow-route': !showFilterContent }" @click="showFilterContent = !showFilterContent" />
      </div>
      <el-collapse-transition>
        <div v-show="showFilterContent">
          <div class="filter-content">
            <div class="filter-item">
              <div class="filter-item-label">{{ $t('operation.business_name') }}</div>
              <ScopeSelect
                :value="search.business_id"
                class="filter-item-value"
                :is-remote="true"
                :http-request-method="searchBusinessNameOptions"
                :remote-params="getBusinessSearchParams"
                :option-label="formatBusiness"
                :select-placeholder="$t('operation.select_business')"
                @updateValue="search.business_id = $event"
              />
            </div>
            <div class="filter-item">
              <div class="filter-item-label">{{ $t('operation.business_type') }}</div>
              <el-select v-model="search.business_type" class="filter-item-value" size="small" clearable :placeholder="$t('operation.select_business_type')">
                <el-option v-for="businessType in businessTypeOptions" :key="businessType.value" :label="businessType.label" :value="businessType.value" />
              </el-select>
            </div>
            <div class="filter-item" />
          </div>
          <div class="filter-operate">
            <div class="filter-operate-left">
              <el-button type="primary" size="small" icon="el-icon-plus" @click="openCreateDialog">{{ $t('operation.create_business') }}</el-button>
            </div>
            <div class="filter-operate-right">
              <el-button size="small" @click="resetSearch">{{ $t('components.clear') }}</el-button>
              <el-button type="primary" size="small" icon="el-icon-search" @click="searchBusinesses">{{ $t('components.search') }}</el-button>
            </div>
          </div>
        </div>
      </el-collapse-transition>
    </div>
    <div class="tableContainer">
      <el-table v-loading="listLoading" :data="tableData" stripe fit highlight-current-row style="width: 100%">
        <el-table-column prop="business_no" :label="$t('operation.business_no')" min-width="150" align="center" />
        <el-table-column prop="business_name" :label="$t('operation.business_name')" min-width="180" align="center" />
        <el-table-column :label="$t('operation.business_type')" min-width="110" align="center">
          <template slot-scope="scope">{{ getBusinessTypeLabel(scope.row.business_type) }}</template>
        </el-table-column>
        <el-table-column prop="country_code" :label="$t('operation.business_region')" min-width="150" align="center" />
        <el-table-column prop="currency" :label="$t('operation.business_currency')" min-width="130" align="center" />
        <el-table-column :label="$t('operation.related_franchisee')" min-width="220" align="center">
          <template slot-scope="scope">
            <el-tooltip :content="formatFranchisee(scope.row)" placement="top" :disabled="formatFranchisee(scope.row) === '-'">
              <span v-if="hasFranchisee(scope.row)" class="franchisee-cell">
                <span class="franchisee-no">{{ scope.row.franchisee_no }}</span>
                <span class="franchisee-name">{{ scope.row.franchisee_name }}</span>
              </span>
              <span v-else>-</span>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column prop="create_time" :label="$t('operation.create_time')" min-width="170" align="center" />
        <el-table-column :label="$t('components.operation')" min-width="150" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="openDetailDialog(scope.row)">{{ $t('components.detail') }}</el-button>
            <el-button type="text" size="small" @click="openEditDialog(scope.row)">{{ $t('components.edit') }}</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-container">
        <paginator v-if="!listLoading" :list-query="listQuery" @paginator-change="onPageChange" />
      </div>
    </div>

    <business-form-dialog ref="businessFormDialog" @success="getBusinessList" />
    <business-detail-dialog ref="businessDetailDialog" />
  </div>
</template>

<script>
import { fetchBusinessList, searchBusinesses } from '@/api/operation-manage/business'
import { filterEmptyAttr } from '@/utils/common'
import { businessTypeMap } from '@/utils/maps'

export default {
  name: 'Business_Manage_List',
  components: {
    Paginator: () => import('@/views/components/Paginator'),
    BusinessFormDialog: () => import('./components/BusinessFormDialog.vue'),
    BusinessDetailDialog: () => import('./components/BusinessDetailDialog.vue')
  },
  data() {
    return {
      showFilterContent: true,
      listLoading: false,
      search: {
        business_name: '',
        business_type: ''
      },
      tableData: [],
      listQuery: {
        page: 1,
        limit: 15,
        total: 0
      }
    }
  },
  computed: {
    businessTypeOptions() {
      return businessTypeMap()
    }
  },
  created() {
    this.getBusinessList()
  },
  methods: {
    searchBusinessNameOptions(params) {
      return searchBusinesses(params)
    },
    getBusinessSearchParams(keyword) {
      return { keyword, size: 20 }
    },
    formatBusiness(business) {
      return [business.business_no, business.business_name].filter(Boolean).join('/')
    },
    getBusinessList(otherParams = {}) {
      this.listLoading = true
      const { page, limit, ...filters } = { ...this.search, ...this.listQuery, ...otherParams }
      const params = filterEmptyAttr({ ...filters, page, size: limit })
      fetchBusinessList(params).then(res => {
        const data = res.data || {}
        this.tableData = data.list || []
        this.listQuery.total = data.total || 0
        this.listQuery.page = data.current || this.listQuery.page
        this.listQuery.limit = data.size || this.listQuery.limit
      }).finally(() => {
        this.listLoading = false
      })
    },
    searchBusinesses() {
      this.listQuery.page = 1
      this.getBusinessList()
    },
    resetSearch() {
      this.search = { business_name: '', business_type: '' }
      this.searchBusinesses()
    },
    onPageChange() {
      this.getBusinessList()
    },
    getBusinessTypeLabel(businessType) {
      const option = this.businessTypeOptions.find(typeOption => typeOption.value === Number(businessType))
      return option ? option.label : businessType || '-'
    },
    hasFranchisee(business) {
      return Boolean(business.franchisee_no || business.franchisee_name)
    },
    formatFranchisee(business) {
      if (this.hasFranchisee(business)) {
        return [business.franchisee_no, business.franchisee_name].filter(Boolean).join('/')
      }
      return '-'
    },
    openCreateDialog() {
      this.$refs.businessFormDialog.showModal()
    },
    openEditDialog(business) {
      this.$refs.businessFormDialog.showModal(business)
    },
    openDetailDialog(business) {
      this.$refs.businessDetailDialog.showModal(business)
    }
  }
}
</script>

<style lang="scss" scoped>
.page-container {
  margin: 40px 57px 50px 32px;
}
.page-title {
  padding-bottom: 24px;
  border-bottom: 1px solid #D3D7DF;
}
.filter-content .filter-item {
  width: 30%;
}
.franchisee-cell {
  display: inline-flex;
  max-width: 190px;
  flex-direction: column;
  align-items: center;
  line-height: 20px;
  vertical-align: middle;
}
.franchisee-no,
.franchisee-name {
  max-width: 190px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
